"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import type { CoverLetterData } from "@/lib/coverLetterEditor/types";
import { saveCoverLetter } from "@/lib/coverLetterEditor/storage";
import { COVER_LETTER_TEMPLATES, getCoverLetterTemplate } from "@/lib/coverLetterEditor/templates";
import EditableText from "@/components/resumeEditor/EditableText";
import { trackEvent } from "@/lib/analytics/track";

const DEFAULT_TEMPLATE = COVER_LETTER_TEMPLATES[0];

function newId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export default function CoverLetterEditorShell({ initialData }: { initialData: CoverLetterData }) {
  const router = useRouter();
  const [data, setData] = useState(initialData);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTemplates, setShowTemplates] = useState(false);

  function update<K extends keyof CoverLetterData>(key: K, value: CoverLetterData[K]) {
    setData((prev) => {
      const next = { ...prev, [key]: value };
      saveCoverLetter(next);
      return next;
    });
  }

  function updateParagraph(index: number, value: string) {
    setData((prev) => {
      const paragraphs = [...prev.paragraphs];
      paragraphs[index] = value;
      const next = { ...prev, paragraphs };
      saveCoverLetter(next);
      return next;
    });
  }

  function addParagraph() {
    setData((prev) => {
      const next = { ...prev, paragraphs: [...prev.paragraphs, ""] };
      saveCoverLetter(next);
      return next;
    });
  }

  function removeParagraph(index: number) {
    setData((prev) => {
      const next = { ...prev, paragraphs: prev.paragraphs.filter((_, i) => i !== index) };
      saveCoverLetter(next);
      return next;
    });
  }

  function setTemplate(templateId: string) {
    const t = getCoverLetterTemplate(templateId);
    if (!t) return;
    setData((prev) => {
      const next = {
        ...prev,
        meta: { ...prev.meta, templateId: t.id, accentColor: t.accentColor, fontFamily: t.fontFamily },
      };
      saveCoverLetter(next);
      return next;
    });
    setShowTemplates(false);
  }

  async function handleDownload() {
    setError(null);
    setDownloading(true);
    try {
      const [{ pdf }, { default: CoverLetterPdf }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("@/components/coverLetterEditor/pdf/CoverLetterPdf"),
      ]);
      const blob = await pdf(<CoverLetterPdf data={data} />).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const fileName = (data.fullName || "cover-letter").toLowerCase().replace(/[^a-z0-9]+/g, "-");
      a.download = `${fileName}-cover-letter.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      trackEvent("download_click", { docType: "cover_letter" });
    } catch {
      setError("Couldn't generate the PDF — please try again.");
    } finally {
      setDownloading(false);
    }
  }

  function startNew() {
    const now = new Date().toISOString();
    const blank: CoverLetterData = {
      id: newId(),
      fullName: "",
      email: "",
      jobTitle: "",
      date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
      salutation: "Dear Hiring Manager,",
      paragraphs: [""],
      closing: "Sincerely,",
      meta: {
        templateId: DEFAULT_TEMPLATE.id,
        accentColor: DEFAULT_TEMPLATE.accentColor,
        fontFamily: DEFAULT_TEMPLATE.fontFamily,
        createdAt: now,
        updatedAt: now,
      },
    };
    saveCoverLetter(blank);
    router.push(`/cover-letter-builder?id=${blank.id}`);
  }

  const template = getCoverLetterTemplate(data.meta.templateId) ?? DEFAULT_TEMPLATE;
  const { accentColor, fontFamily } = data.meta;
  const isBlock = template.layout === "block";
  const isCentered = template.layout === "centered";
  const isFormal = template.layout === "formal-serif";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0b1220]">
      {/* Toolbar */}
      <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur-sm dark:border-slate-800 dark:bg-[#0f172a]/90">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-6 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">✉️ Cover Letter Editor</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTemplates((v) => !v)}
                className="flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 dark:border-slate-700 dark:text-slate-300"
              >
                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: accentColor }} />
                {template.label}
              </button>
              {showTemplates ? (
                <div className="absolute right-0 top-full z-30 mt-2 w-72 rounded-2xl border border-slate-200 bg-white p-2 shadow-xl dark:border-slate-800 dark:bg-[#0f172a]">
                  {COVER_LETTER_TEMPLATES.map((t) => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTemplate(t.id)}
                      className={`flex w-full items-start gap-3 rounded-xl p-2.5 text-left transition hover:bg-slate-50 dark:hover:bg-slate-800 ${
                        t.id === template.id ? "bg-violet-50 dark:bg-violet-500/10" : ""
                      }`}
                    >
                      <span
                        className="mt-0.5 h-8 w-8 flex-shrink-0 rounded-lg border border-black/5"
                        style={{ backgroundColor: t.preview.bg }}
                      >
                        <span className="block h-1.5 w-full rounded-t-lg" style={{ backgroundColor: t.preview.accent }} />
                      </span>
                      <span>
                        <span className="flex items-center gap-1.5">
                          <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">{t.label}</span>
                          <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                            {t.tag}
                          </span>
                        </span>
                        <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">{t.description}</span>
                      </span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={startNew}
              className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 dark:border-slate-700 dark:text-slate-300"
            >
              + New Letter
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-600 px-4 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:brightness-110 disabled:opacity-60"
            >
              {downloading ? "Generating…" : "Download PDF"}
            </button>
          </div>
        </div>
        {error ? <p className="mx-auto max-w-5xl px-6 pb-2 text-xs text-rose-500">{error}</p> : null}
      </div>

      {/* Preview / editor surface */}
      <div className="mx-auto max-w-3xl px-6 py-10">
        <div
          className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xl dark:border-slate-800 dark:bg-[#0f172a]"
          style={{ fontFamily }}
        >
          {/* Header block — structure varies per template */}
          {isBlock ? (
            <div className="px-10 py-8" style={{ backgroundColor: accentColor }}>
              <EditableText
                value={data.fullName}
                onChange={(v) => update("fullName", v)}
                as="div"
                placeholder="Your Name"
                className="text-2xl font-bold text-white"
              />
              <div className="mt-1 flex flex-wrap gap-1 text-sm text-white/80">
                <EditableText value={data.email} onChange={(v) => update("email", v)} placeholder="you@example.com" />
                <span>·</span>
                <EditableText value={data.phone ?? ""} onChange={(v) => update("phone", v)} placeholder="Phone (optional)" />
              </div>
            </div>
          ) : (
            <div className={`px-10 pt-10 ${isCentered ? "text-center" : ""}`}>
              <EditableText
                value={data.fullName}
                onChange={(v) => update("fullName", v)}
                as="div"
                placeholder="Your Name"
                className={`font-bold text-slate-900 dark:text-white ${isCentered ? "text-3xl uppercase tracking-wide" : "text-2xl"}`}
              />
              <div
                className={`mt-1 flex flex-wrap gap-1 text-sm text-slate-500 dark:text-slate-400 ${isCentered ? "justify-center" : ""}`}
              >
                <EditableText value={data.email} onChange={(v) => update("email", v)} placeholder="you@example.com" />
                <span>·</span>
                <EditableText value={data.phone ?? ""} onChange={(v) => update("phone", v)} placeholder="Phone (optional)" />
              </div>
              {template.layout === "divider" ? (
                <div className="mt-4 border-b-2" style={{ borderColor: accentColor }} />
              ) : isCentered ? (
                <div className="mx-auto mt-4 h-px w-24 bg-slate-300 dark:bg-slate-700" />
              ) : (
                <div className="mt-4 border-b border-slate-300 dark:border-slate-700" />
              )}
            </div>
          )}

          <div className="px-10 pb-10 pt-6">
            <div className={isFormal ? "text-right" : ""}>
              <EditableText
                value={data.date}
                onChange={(v) => update("date", v)}
                className="text-sm text-slate-700 dark:text-slate-300"
              />
            </div>

            <div className="mt-4 space-y-0.5 text-sm text-slate-700 dark:text-slate-300">
              <EditableText
                value={data.recipientName ?? ""}
                onChange={(v) => update("recipientName", v)}
                as="div"
                placeholder="Recipient Name (optional)"
              />
              <EditableText
                value={data.recipientCompany ?? ""}
                onChange={(v) => update("recipientCompany", v)}
                as="div"
                placeholder="Company (optional)"
              />
            </div>

            <div className="mt-6 text-sm text-slate-700 dark:text-slate-300">
              <EditableText value={data.salutation} onChange={(v) => update("salutation", v)} />
            </div>

            <div className="mt-4 space-y-4">
              {data.paragraphs.map((p, i) => (
                <div key={i} className="group relative">
                  <EditableText
                    value={p}
                    onChange={(v) => updateParagraph(i, v)}
                    as="p"
                    multiline
                    placeholder="Write this paragraph…"
                    className="text-sm leading-relaxed text-slate-700 dark:text-slate-300"
                  />
                  {data.paragraphs.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => removeParagraph(i)}
                      className="absolute -right-7 top-0 hidden text-xs text-slate-400 hover:text-rose-500 group-hover:block"
                      aria-label="Remove paragraph"
                    >
                      ✕
                    </button>
                  ) : null}
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={addParagraph}
              className="mt-4 text-xs font-semibold text-violet-600 hover:underline dark:text-violet-400"
            >
              + Add paragraph
            </button>

            <div className="mt-8 text-sm text-slate-700 dark:text-slate-300">
              <EditableText value={data.closing} onChange={(v) => update("closing", v)} />
              <p className="mt-1 font-semibold text-slate-900 dark:text-white">{data.fullName || "Your Name"}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
